const study = {
  meta: {
    slug: "bad-credit-restaurant-revenue-financing-95k",
    title:
      "$95K when the bank said no: How a Miami restaurant used revenue financing to rebuild — then refinanced into a real line of credit 8 months later",
    excerpt:
      "A hurricane, a personal FICO of 590, and a broken walk-in freezer at the worst possible moment. Here's how we structured a two-stage file: fast, expensive capital to survive the season, then a graduated refinance into working capital once the credit had recovered.",
    industry: "Restaurant · Full-service Latin American",
    product: "Revenue Financing → refinance into Working Capital LOC",
    amount: "$95K → $150K refi",
    term: "12 months → 18 months",
    timeToFund: "5 hours (initial) · 4 days (refi)",
    state: "Florida",
    challenge: "Bad-credit turnaround + credit rebuild",
    image:
      "https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-07-03",
    readTime: "7 min read",
    tags: ["bad-credit"],
    relatedArticles: [
      "bad-credit-business-loans-2026",
      "how-mca-factor-rates-work",
      "true-cost-of-merchant-cash-advance",
      "business-credit-vs-personal-credit",
    ],
  },
  situation: `Eleven years in business. Sit-down Latin American concept in a Miami neighborhood that had gentrified around them, both a blessing and a target. In 2024, a hurricane took the walk-in freezer offline for six days and cost about $18K in spoiled inventory. Insurance covered $9K after the deductible.

The owner did what a lot of restaurateurs do to survive a bad quarter: **maxed personal credit cards**. Two years of that discipline slipping had dragged the personal FICO from **718 down to 590**. Business revenue was still healthy — $1.9M trailing 12 months, 3.2M in gross deposits — but every conventional lender we approached read the personal credit report first and hung up second.

The immediate need: **$85K for a new walk-in unit and adjacent kitchen repairs** before summer season. Every day without it meant menu reductions and a slow bleed of catering revenue.`,
  structure: `**Stage 1 — Survive the season (Revenue Financing, 12 months).** With a 590 FICO, the only speed-plus-scale products available were revenue-based: a lender fronts a lump sum, and gets paid back as a percentage of daily card and ACH deposits until a fixed payback amount is reached.

The math the borrower needed to see clearly:

- **$95K advance**, factor rate **1.38** → total payback **$131,100**
- **Expected term: ~12 months** based on trailing deposit volume
- **Daily debit**: ~10% of daily card sales, capped
- **Effective APR (avg-outstanding-balance method): ~104%**

We were transparent about the cost. This is not cheap money. But three things made it the right product for this stage:

1. **No personal credit weight** — the underwriter was pricing the business's deposit consistency, not the owner's FICO
2. **Speed** — 5 hours from signed app to funded, before the kitchen problem cost another weekend of revenue
3. **A written exit plan** — the loan agreement contained a **prepayment discount clause** we negotiated in: if refinanced with any documentable working-capital product before month 9, the payback drops by 8% ($10,488 back to the borrower)

**Stage 2 — Rebuild while paying down (personal credit playbook, months 1-8).** While the business paid down the revenue advance, we worked in parallel on the credit file:

- **Pay down revolving utilization** below 30% on the personal cards using the freed-up cash flow — this alone moved the FICO 42 points in 90 days
- **Add one secured trade line** ($500 secured card, on-time payments only)
- **Do NOT open new personal credit** during the rebuild period
- **Establish two business trade lines** with restaurant suppliers who report to Dun & Bradstreet — this began building a separate business credit profile that lenders could underwrite independently

By month 7 the personal FICO was **at 671**, tri-merge report clean of late-marks.

**Stage 3 — Refinance (Working Capital LOC, 18 months).** With the profile repaired, we routed the file to a fintech lender who prices off business cash flow more than personal credit:

- **$150K working-capital line of credit** at 24-month draw / 18-month full repay
- **Priced at ~18% APR** — a 5.8x cost improvement over the revenue advance
- **Structured to pay off the remaining revenue advance in full** (with the 8% prepayment discount applied)
- **Left $57K of the LOC available** as a genuine seasonal buffer

**What we intentionally didn't do:** we did not try to save money by delaying stage 1. Waiting for the credit to recover before financing the walk-in would have cost another summer season of reduced menu and lost catering. Sometimes the honest broker's job is to say "yes, this is expensive, and it's still the right choice today."`,
  outcome: `**Nine months from initial funding to refinanced.** By the refi date:

- **Revenue advance paid off in full** with the 8% prepayment discount recaptured (~$10,500 back to the operator)
- **Personal FICO** recovered from **590 → 674** (up 84 points in 8 months)
- **Business credit file established** with three trade lines reporting positive
- **Menu restored to full 42 items** by month 3, catering revenue up 19% vs. prior year by end of season
- **Working-capital LOC** available for the next hurricane season with 6.6× lower cost of capital than the emergency product

The lesson: **the cheap-money answer is not the correct answer if it doesn't exist yet.** Bad credit is a temporary condition. The right sequence is: expensive-but-fast to survive, structured rebuild to qualify, then refinance into the priced-right product 6–9 months later. The cost of the first product is the tuition; the second product is the payoff.`,
};

export default study;
